import React, { useState } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  font-family: ${theme.typography.fontFamily};
  padding: ${theme.spacing.xl};
`;

const Modal = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.xxxl};
  max-width: 480px;
  width: 100%;
  box-shadow: ${theme.shadows.lg};
  border: 1px solid ${theme.colors.border};
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${theme.spacing.lg};
  right: ${theme.spacing.lg};
  background: transparent;
  border: none;
  font-size: ${theme.typography.fontSizes.xxl};
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  padding: ${theme.spacing.sm};
  line-height: 1;
  transition: color 0.2s ease;
  
  &:hover {
    color: ${theme.colors.text};
  }
`;

const TrialBadge = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%);
  color: white;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.lg};
  text-align: center;
  font-size: ${theme.typography.fontSizes.base};
  font-weight: ${theme.typography.fontWeights.bold};
  margin-bottom: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
`;

const Title = styled.h2`
  margin: 0 0 ${theme.spacing.sm} 0;
  color: ${theme.colors.text};
  text-align: center;
  font-size: ${theme.typography.fontSizes.xxxl};
  font-weight: ${theme.typography.fontWeights.bold};
`;

const Subtitle = styled.p`
  margin: 0 0 ${theme.spacing.lg} 0;
  color: ${theme.colors.textSecondary};
  text-align: center;
  font-size: ${theme.typography.fontSizes.base};
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Label = styled.label`
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.base};
  font-weight: ${theme.typography.fontWeights.medium};
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  font-size: ${theme.typography.fontSizes.sm};
  text-align: center;
  padding: ${theme.spacing.sm};
  background-color: rgba(255, 85, 85, 0.1);
  border-radius: ${theme.borderRadius.sm};
  border: 1px solid ${theme.colors.error};
`;

const SuccessMessage = styled.div`
  color: ${theme.colors.success};
  font-size: ${theme.typography.fontSizes.sm};
  text-align: center;
  padding: ${theme.spacing.sm};
  background-color: rgba(80, 250, 123, 0.1);
  border-radius: ${theme.borderRadius.sm};
  border: 1px solid ${theme.colors.success};
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${theme.spacing.md} 0 ${theme.spacing.lg} 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSizes.sm};
  
  &:before {
    content: '✓';
    color: ${theme.colors.success};
    font-weight: bold;
    font-size: ${theme.typography.fontSizes.lg};
    flex-shrink: 0;
  }
`;

const SignupModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const validatePassword = () => {
    if (password.length < 6) {
      return 'Password must be at least 6 characters';
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    const passwordError = validatePassword();
    if (passwordError) {
      setError(passwordError);
      setIsLoading(false);
      return;
    }

    const result = await signup(email, password, displayName);
    
    if (result.success) {
      setSuccess('Account created! Starting your free trial...');
      setTimeout(() => {
        onClose();
        navigate('/');
      }, 1500);
    } else {
      setError(result.error || 'Signup failed');
    }
    
    setIsLoading(false);
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        
        <TrialBadge>7-Day Free Trial - No Credit Card Required</TrialBadge>
        
        <Title>Create Your Account</Title>
        <Subtitle>Join and start labeling today</Subtitle>
        
        <FeaturesList>
          <FeatureItem>Unlimited access during trial</FeatureItem>
          <FeatureItem>All annotation tools included</FeatureItem>
          <FeatureItem>Cancel anytime</FeatureItem>
        </FeaturesList>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Display Name (Optional)</Label>
            <Input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password (min 6 characters)"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Confirm Password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
            />
          </FormGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <Button 
            type="submit" 
            disabled={isLoading}
            variant="primary"
            style={{ marginTop: theme.spacing.md }}
          >
            {isLoading ? 'Creating Account...' : 'Start Free Trial'}
          </Button>
        </Form>
      </Modal>
    </Overlay>
  );
};

export default SignupModal;

